import React, { useState, useEffect, useRef } from 'react';
import InviteCode from './InviteCode';
import ParticipantForm from './ParticipantForm';
import MountainForm from './MountainForm';
import EmergencyContactForm from './EmergencyContactForm';
import RegistrationComplete from './RegistationComplete';
import MainMountain from './MainMountain'
import InviteeMountain from './InviteeMountain';

// Map backend step to frontend numeric step
const BACKEND_STEP_TO_FRONTEND_SINGLE = {
  INVITE_CODE: 2,
  MAIN_PERSONAL_INFO: 2,
  MAIN_MOUNTAIN_INFO: 3,
  EMERGENCY_CONTACT: 4,
  FINAL: 10,
};

const BACKEND_STEP_TO_FRONTEND_DOUBLE = {
  INVITE_CODE: 5,
  MAIN_PERSONAL_INFO: 5,
  MAIN_MOUNTAIN_INFO: 6,
  INVITEE_PERSONAL_INFO: 7,
  INVITEE_MOUNTAIN_INFO: 8,
  EMERGENCY_CONTACT: 9,
  FINAL: 10,
};


// Resolve frontend step from backend step + role
function resolveFrontendStep(currentStep, activeParticipantRole, inviteType, mainParticipant, invitee) {
  if (!currentStep) return 1;

  console.log("Inside RESOLVE FRONTEND STEP");
  console.log("Passed Invite Type", inviteType);

  // Determine the next step based on invite type (single or double)
  let mapping;  // Declare mapping variable outside the if block

  if (inviteType === 'single') {
    mapping = BACKEND_STEP_TO_FRONTEND_SINGLE[currentStep];
  } else if (inviteType === 'double') {
    mapping = BACKEND_STEP_TO_FRONTEND_DOUBLE[currentStep];
  }

  console.log("Mapping from backend step to frontend:", mapping);

  if (typeof mapping === 'number') return mapping;

  if (typeof mapping === 'object') {
    const role = activeParticipantRole ?? (invitee ? 'INVITEE' : 'MAIN');
    return mapping[role] ?? 1;
  }

  return 1;
}



const MainForm = () => {
  const [step, setStep] = useState(1);
  const [Flow, setFlow] = useState(null);
  const [token, setToken] = useState(null);
  const [mainParticipant, setMainParticipant] = useState({});
  const [invitee, setInvitee] = useState(null);
  const [finalParticipantId, setFinalParticipantId] = useState(null);
  const [inviteType, setInviteType] = useState(null);
  const [mainNeedsMountain, setMainNeedsMountain] = useState(false);
  const [inviteeNeedsMountain, setInviteeNeedsMountain] = useState(false);
  const [mainParticipantId, setMainParticipantId] = useState(null);
  const [inviteeParticipantId, setInviteeParticipantId] = useState(null);
  const [FlowParticipantId, setFlowParticipantId] = useState(null);
  const [genderMainParticipant, setGenderMainParticipant] = useState(null);
  const [genderInvitee, setGenderInvitee] = useState(null);

    // --- Derived participant IDs for rendering ---
  const effectiveMainId = FlowParticipantId ?? mainParticipantId;
  const effectiveInviteeId = inviteeParticipantId ?? invitee?.participantId ?? null;

  const prevStepRef = useRef(step);

  // Reset mountain checkbox when entering step 2 (main participant)
  useEffect(() => {
    if (step === 2 && prevStepRef.current !== 2) {
      setMainNeedsMountain(false);
      setInviteeNeedsMountain(false);
    }
    prevStepRef.current = step;
  }, [step]);

  // Only reset if token disappears
  useEffect(() => {
    if (!token) setStep(1);
  }, [token]);

  const handleBackendStep = (backendData) => {
  const {
    participant_id,
    current_step,
    active_participant_role,
    registration_token,
    main_participant_id,
    invitee_participant_id,
    main_needs_mountain,
    invitee_needs_mountain,
    invite_type,  // Include invite type here
  } = backendData;

  // Update token for resuming registration
  if (registration_token) setToken(registration_token);

  // Restore mountain preferences
  if (typeof main_needs_mountain === 'boolean') setMainNeedsMountain(main_needs_mountain);
  if (typeof invitee_needs_mountain === 'boolean') setInviteeNeedsMountain(invitee_needs_mountain);



  // Compute effective participant IDs immediately
  const effectiveMainId = main_participant_id ?? (active_participant_role === 'MAIN' ? participant_id : null);
  const effectiveInviteeId = invitee_participant_id ?? (active_participant_role === 'INVITEE' ? participant_id : null);

  // Update state with IDs
  if (effectiveMainId) {
    setMainParticipantId(effectiveMainId);
    setFlowParticipantId(effectiveMainId); // ensures mountain forms render
    setMainParticipant((prev) => ({ ...(prev || {}), participantId: effectiveMainId }));
  }

  if (effectiveInviteeId) {
    setInviteeParticipantId(effectiveInviteeId);
    setInvitee((prev) => ({ ...(prev || {}), participantId: effectiveInviteeId }));
  }

  // Determine frontend step based on backend step and role
  const nextStep = resolveFrontendStep(
    current_step,
    active_participant_role,
    invite_type, // pass invite_type to resolve the correct step
    { participantId: effectiveMainId },
    { participantId: effectiveInviteeId }
  );

  console.log("NEXT STEP:", nextStep);

  // Store FINAL participant ID if reached
  if (current_step === 'FINAL' && participant_id) setFinalParticipantId(participant_id);

  // Set the numeric step to trigger the right form
  setStep(nextStep);
};

  // console.log("FLOW PARTICIPANT:", FlowParticipantId);
  return (
  <div
    className="container-fluid mainForm p-2"
    style={{
      width: '100%',
      backgroundColor: 'rgba(245, 253, 255, 0.66)',
      borderRadius: '8px',
      boxShadow: '0 2px 8px #161819',
    }}
  >
    {/* Step 1 — Invite Code */}
    {step === 1 && (
      <InviteCode
        onValidated={(data) => {
          setToken(data.registration_token);
          setInviteType(data.invite_type);
          const flow = data.invite_type.toLowerCase();
          setFlow(flow);
          handleBackendStep({ ...data, flow });
        }}
      />
    )}

    {/* SINGLE FLOW */}
    {Flow === 'single' && (
      <>
      {/* Step 2 — Main participant */}
        {step === 2 && (
          <ParticipantForm
            title="Datos Personales"
            token={token}
            preloadedData={mainParticipant}
            showMountainOption={true}
            needsMountainForm={mainNeedsMountain}
            onToggleMountain={(value) => {
              console.log('[Main toggleMountain]', value);
              setMainNeedsMountain(value);
            }}
            onRegistered={(result) => {
              setMainParticipantId(result.main_participant_id);
              setMainParticipant(result);
              setFlowParticipantId(result.main_participant_id);
              setGenderMainParticipant(result.gender_main);
              setStep(mainNeedsMountain ? 3 : 4);
            }}
            txtBtn="Continuar"
          />
        )}

        {/* Step 3 — Main Mountain */}
        {/* {step === 3 && effectiveMainId && mainNeedsMountain && ( */}
        {step === 3 && mainNeedsMountain && (
          <MainMountain
            token={token}
            flow={Flow}
            FlowParticipantId={effectiveMainId}
            inviteeParticipantId={effectiveInviteeId}
            mainNeedsMountain={mainNeedsMountain}
            inviteeNeedsMountain={inviteeNeedsMountain}
            genderMainParticipant={genderMainParticipant}
            onSubmitted={() => setStep(4)}
            onSkip={() => setStep(4)}
          />
        )}

        {/* Step 4 — EmergencyContact */}
        {step === 4 && (
          <EmergencyContactForm
            token={token}
            participantId={effectiveMainId}
            preloadedData={mainParticipant?.emergency_contact}
            onSubmitted={() => setStep(10)}
          />
        )}
      </>
    )}

    {/* DOUBLE FLOW */}
    {Flow === 'double' && (
      <>
        {/* Step 2 — Main participant */}
        {step === 5 && (
          <ParticipantForm
            title="Primer Participante"
            token={token}
            preloadedData={mainParticipant}
            showMountainOption={true}
            needsMountainForm={mainNeedsMountain}
            onToggleMountain={(value) => {
              console.log('[Main toggleMountain]', value);
              setMainNeedsMountain(value);
            }}
            onRegistered={(result) => {
              setMainParticipantId(result.main_participant_id);
              setMainParticipant(result);
              setFlowParticipantId(result.main_participant_id);
              setGenderMainParticipant(result.gender_main);
              console.log('Main need mountain', mainNeedsMountain)
              setStep(mainNeedsMountain ? 6 : 7);
            }}
            txtBtn="Continuar"
          />
        )}

        {/* Step 5 — Main Mountain */}
        {step === 6 &&  mainNeedsMountain && (
          <MainMountain
            token={token}
            flow={Flow}
            FlowParticipantId={effectiveMainId}
            inviteeParticipantId={effectiveInviteeId}
            mainNeedsMountain={mainNeedsMountain}
            inviteeNeedsMountain={inviteeNeedsMountain}
            genderMainParticipant={genderMainParticipant}
            genderInvitee={genderInvitee}
            onSubmitted={() => setStep(7)}
            onSkip={() => setStep(7)}
          />
        )}

        {/* Step 6 — Invitee participant */}
        {step === 7 && (
          <ParticipantForm
            title="Segundo Participante"
            token={token}
            preloadedData={invitee}
            showMountainOption={true}
            needsMountainForm={inviteeNeedsMountain}
            onToggleMountain={(value) => {
              console.log('[Invitee toggleMountain]', value);
              setInviteeNeedsMountain(value);
            }}
            onRegistered={(result) => {
              setInviteeParticipantId(result.participant_id);
              setInvitee(result);
              setGenderInvitee(result.gender_invitee);
              setMainNeedsMountain(false);
              setStep(inviteeNeedsMountain ? 8 : 9);
            }}
            txtBtn="Continuar"
          />
        )}

        {/* Step 7 — Invitee Mountain */}
        {step === 8 && effectiveInviteeId && inviteeNeedsMountain && (
          <InviteeMountain
            token={token}
            flow={Flow}
            FlowParticipantId={effectiveMainId}
            inviteeParticipantId={effectiveInviteeId}
            mainNeedsMountain={mainNeedsMountain}
            inviteeNeedsMountain={inviteeNeedsMountain}
            genderMainParticipant={genderMainParticipant}
            genderInvitee={genderInvitee}
            onSubmitted={() => setStep(9)}
            onSkip={() => setStep(9)}
          />
        )}

        {/* Step 8 — EmergencyContact */}
        {step === 9 && (
          <EmergencyContactForm
            token={token}
            participantId={effectiveMainId}
            preloadedData={mainParticipant?.emergency_contact}
            onSubmitted={() => setStep(10)}
          />
        )}
      </>
    )}

    {/* Step 9 — RegistrationComplete */}
    {step === 10 && (
      <RegistrationComplete token={token} participantId={finalParticipantId} />
    )}
  </div>
);

};

export default MainForm;
